import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';

import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import './ViewSaleModalStyles.css';
import './AllSellStyles.css';
import apiService from '../../services/api';
import AddPurchase from './AddPurchase';
import AddPurchaseReturn from './AddPurchaseReturn';
import AddPurchaseRequisition from './AddPurchaseRequisition';
import PurchaseOrder from './PurchaseOrder';
import PurchaseRequisition from './PurchaseRequisition';
import ListPurchaseReturn from './ListPurchaseReturn';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import invoiceGenerator from '../../services/invoiceGenerator';
import LabelGenerator from './LabelGenerator';
import AddPaymentModal from './AddPaymentModal';
import ViewPaymentsModal from './ViewPaymentsModal';
import UpdatePurchaseStatusModal from './UpdatePurchaseStatusModal';
import PurchaseNotificationModal from './PurchaseNotificationModal';
import ViewPurchaseModal from './ViewPurchaseModal';

const Purchases = ({ activeTab: propActiveTab }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list_purchases');

  // Synchronize state with prop changes from dashboard popover
  useEffect(() => {
    if (propActiveTab) {
      if (propActiveTab === 'purchases') {
        setActiveTab('list_purchases');
      } else {
        setActiveTab(propActiveTab);
      }
    }
  }, [propActiveTab]);

  // Shared State
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // List Purchases State
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [openActionId, setOpenActionId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [productsForLabels, setProductsForLabels] = useState([]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showViewPaymentsModal, setShowViewPaymentsModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isLoadingView, setIsLoadingView] = useState(false);

  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleDropdown = (purchaseId, event) => {
    event.stopPropagation();
    if (openActionId === purchaseId) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(purchaseId);
    }
  };


  // Filter state
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [filterSupplierId, setFilterSupplierId] = useState('all');
  const [filterLocationId, setFilterLocationId] = useState('all');
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Debounce search input so API is not hit on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch Purchases
  useEffect(() => {
    if (activeTab === 'list_purchases') {
      fetchPurchases();
    }
  }, [activeTab, currentPage, perPage, debouncedSearchTerm]);

  // Fetch locations and suppliers for filters
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const locResp = await apiService.getLocations();
        setLocations(locResp?.data || locResp || []);
      } catch (e) { /* console.warn('Failed to fetch locations:', e); */ }
      try {
        const supResp = await apiService.getContacts();
        const contactList = supResp?.data || supResp || [];
        setSuppliers(contactList.filter(c => c.type === 'supplier' || c.type === 'both'));
      } catch (e) { /* console.warn('Failed to fetch suppliers:', e); */ }
    };
    fetchFilterData();
  }, []);

  const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      /* console.log('Fetching purchases...'); */
      
      let locId = null;
      let bizId = null;
      
      try {
        const userDetails = await apiService.getUserDetails();
        const userData = userDetails.data || userDetails;
        setLoggedInUser(userData);
        locId = userData.location_id;
        bizId = userData.business_id;
      } catch (err) {
      /* console.warn('Could not fetch user details for filtering:', err); */
      }

      const formatForApi = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const now = new Date();
      const startDateDefault = new Date();
      startDateDefault.setFullYear(now.getFullYear() - 2); // 2 years ago

      const params = {
        page: currentPage,
        per_page: perPage,
      };

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPaymentStatus !== 'all') params.payment_status = filterPaymentStatus;
      if (filterSupplierId !== 'all') params.supplier_id = filterSupplierId;
      if (filterLocationId !== 'all') params.location_id = filterLocationId;
      /* console.log('Final request params:', params); */
      const data = await apiService.getPurchases(params);
      /* console.log('Purchases API response:', data); */
      
      // Robust mapping to handle various API response formats (Success wrapper, Laravel pagination, etc.)
      let purchaseList = [];
      if (data.success && data.data) {
        purchaseList = Array.isArray(data.data) ? data.data : (data.data.data || []);
      } else {
        purchaseList = Array.isArray(data) ? data : (data.data || []);
        // Check for double nesting common in paginated results
        if (!Array.isArray(purchaseList) && purchaseList.data) {
          purchaseList = purchaseList.data;
        }
      }
      /* console.log('Mapped purchase list:', purchaseList); */
      setPurchases(Array.isArray(purchaseList) ? purchaseList : []);
      
      // API returns { current_page, data: [...], last_page, total, ... }
      // Pagination info is at the TOP level of the response, not inside data.data
      if (data.last_page) {
        setLastPage(data.last_page);
        setTotalPurchases(data.total);
      } else if (data.meta?.last_page) {
        setLastPage(data.meta.last_page);
        setTotalPurchases(data.meta.total);
      }
    } catch (err) {
      /* console.error('Failed to load purchases', err); */
      setPurchases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveAddedBy = (purchase) => {
    // 1. Try created_by_user object (Relation)
    if (purchase.created_by_user) {
      return `${purchase.created_by_user.first_name || ''} ${purchase.created_by_user.last_name || ''}`.trim() || purchase.created_by_user.username;
    }
    
    // 2. Try created_by object (Flattened or alternate name)
    if (typeof purchase.created_by === 'object' && purchase.created_by) {
      return `${purchase.created_by.first_name || ''} ${purchase.created_by.last_name || ''}`.trim() || purchase.created_by.username;
    }

    // 3. Session Fallback: If IDs match
    if (loggedInUser && (purchase.created_by === loggedInUser.id || purchase.created_by_id === loggedInUser.id)) {
      return `${loggedInUser.first_name || ''} ${loggedInUser.last_name || ''}`.trim() || loggedInUser.username;
    }

    // 4. Other fields
    return purchase.added_by_name || purchase.created_by_name || 'N/A';
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPurchases = (list) => {
    if (!sortConfig.key) return list;
    return [...list].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M7 15l5 5 5-5" /><path d="M7 9l5-5 5 5" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M18 15l-6-6-6 6" />
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  };

  const handleExport = (format) => {
    if (!purchases || purchases.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Action', 'Date', 'Reference No', 'Location', 'Supplier', 'Purchase Status', 'Payment Status', 'Grand Total', 'Payment Due', 'Added By'];
    const rows = purchases.map(p => [
      '', // Action placeholder for export
      p.transaction_date || 'N/A',
      p.ref_no || 'N/A',
      p.location?.name || 'N/A',
      p.supplier?.name || p.contact?.name || 'N/A',
      p.status || 'Pending',
      p.payment_status || 'Due',
      parseFloat(p.final_total || 0).toFixed(2),
      `P: ${parseFloat(p.payment_due || 0).toFixed(2)}${p.return_transaction ? ` / R: ${parseFloat(p.return_transaction.amount_due || 0).toFixed(2)}` : ''}`,
      resolveAddedBy(p)
    ]);

    const title = 'Purchases List';
    const filename = 'purchases';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const buildPurchaseInvoiceData = (purchase) => ({
    invoiceNumber: purchase.invoice_no || purchase.ref_no || '--',
    invoiceDate: purchase.transaction_date ? new Date(purchase.transaction_date).toLocaleDateString() : '--',
    customerName: purchase.contact?.name || purchase.supplier || '--',
    items: (purchase.purchase_lines || []).map(line => ({
      sku: line.product?.sku || line.sub_sku || '',
      name: line.product?.name || ('Product #' + line.product_id),
      quantity: parseFloat(line.quantity || 0),
      unitPrice: parseFloat(line.purchase_price || 0),
      total: parseFloat(line.quantity || 0) * parseFloat(line.purchase_price || 0),
    })),
    payments: (purchase.payment_lines || []).map(p => ({
      method: p.method,
      amount: parseFloat(p.amount || 0),
      date: p.paid_on && !p.paid_on.startsWith('0000') ? new Date(p.paid_on).toLocaleDateString() : '--',
    })),
    subtotal: parseFloat(purchase.total_before_tax || purchase.final_total || 0),
    discount: parseFloat(purchase.discount_amount || 0),
    tax: parseFloat(purchase.tax_amount || 0),
    total: parseFloat(purchase.final_total || 0),
    totalPaid: (purchase.payment_lines || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0),
    amountDue: parseFloat(purchase.payment_due || 0),
    notes: purchase.additional_notes || '',
  });

  const handlePrint = (purchase) => {
    if (purchase && purchase.id) {
      invoiceGenerator.printInvoicePDF(buildPurchaseInvoiceData(purchase));
      setOpenActionId(null);
    } else {
      printPage();
      setShowExportDropdown(false);
    }
  };
  const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };

  const handleAdd = () => {
    if (activeTab === 'list_purchases') {
      setSelectedPurchase(null);
      setActiveTab('add_purchase');
    }
    else if (activeTab === 'purchase_order') navigate('/purchases/order/add');
    else if (activeTab === 'purchase_requisition') setActiveTab('add_purchase_requisition');
    else if (activeTab === 'purchase_return') setActiveTab('add_purchase_return');
  };

  const handleOpenLabels = (purchase) => {
    if (!purchase.purchase_lines || purchase.purchase_lines.length === 0) {
      alert('No products found in this purchase to generate labels.');
      return;
    }

    const mappedProducts = purchase.purchase_lines.map(line => ({
      id: line.product_id,
      name: line.product?.name || `Product #${line.product_id}`,
      sku: line.product?.sku || line.variations?.sub_sku || '--',
      price: parseFloat(line.variations?.sell_price_inc_tax || 0),
      priceExcTax: parseFloat(line.variations?.default_sell_price || 0),
      variation: line.variations?.name || 'Default',
      numberOfLabels: Math.ceil(parseFloat(line.quantity || 1)),
      barcode: line.product?.sku || line.variations?.sub_sku || ''
    }));

    setProductsForLabels(mappedProducts);
    setShowLabelModal(true);
    setOpenActionId(null);
  };

  const handleView = async (purchase) => {
    setOpenActionId(null);
    setSelectedPurchase(purchase);
    setShowViewModal(true);
    setIsLoadingView(true);
    try {
      const response = await apiService.getPurchase(purchase.id);
      let fullPurchase = response.data || response;
      if (Array.isArray(fullPurchase)) fullPurchase = fullPurchase[0];
      setSelectedPurchase(fullPurchase);
    } catch (err) {
      /* console.warn('Could not fetch full purchase for view, using list data:', err); */
    } finally {
      setIsLoadingView(false);
    }
  };

  const handleEdit = async (purchase) => {
    try {
      setIsLoading(true);
      // Fetch fresh details for this specific purchase to ensure full relation data
      const response = await apiService.getPurchase(purchase.id);
      /* console.log('[handleEdit] Raw getPurchase response for id', purchase.id, ':', response); */
      /* console.log('[handleEdit] response.data:', response.data); */
      /* console.log('[handleEdit] response.data?.purchase_lines:', response.data?.purchase_lines); */

      // API returns { data: { id, purchase_lines, ... } }  — unwrap .data first
      let fullPurchase = response.data || response;

      // Also handle array wrapper e.g. [ { id, purchase_lines, ... } ]
      if (Array.isArray(fullPurchase)) {
        fullPurchase = fullPurchase[0];
      }
      /* console.log('[handleEdit] fullPurchase being set as editData:', fullPurchase); */
      /* console.log('[handleEdit] purchase_lines count:', fullPurchase?.purchase_lines?.length); */
      
      setSelectedPurchase(fullPurchase);
      setActiveTab('add_purchase');
    } catch (err) {
      /* console.warn('Error fetching full purchase details:', err); */
      // Fallback to the current purchase object if individual fetch fails
      setSelectedPurchase(purchase);
      setActiveTab('add_purchase');
    } finally {
      setIsLoading(false);
      setOpenActionId(null);
    }
  };

  const handleAddPayment = (purchase) => {
    setSelectedPurchase(purchase);
    setShowAddPaymentModal(true);
    setOpenActionId(null);
  };

  const handleViewPayments = (purchase) => {
    setSelectedPurchase(purchase);
    setShowViewPaymentsModal(true);
    setOpenActionId(null);
  };

  const handleUpdateStatus = (purchase) => {
    setSelectedPurchase(purchase);
    setShowUpdateStatusModal(true);
    setOpenActionId(null);
  };

  const handleShowNotification = (purchase) => {
    setSelectedPurchase(purchase);
    setShowNotificationModal(true);
    setOpenActionId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase?')) {
      try {
        await apiService.deletePurchase(id);
        alert('Purchase deleted successfully');
        fetchPurchases();
      } catch (err) {
        alert('Failed to delete purchase: ' + err.message);
      }
    }
    setOpenActionId(null);
  };

  if (activeTab === 'add_purchase') {
    return (
      <div className="products-container">
        <AddPurchase 
          onBack={() => {
            setActiveTab('list_purchases');
            setSelectedPurchase(null);
            fetchPurchases(); // Correct function name to refresh list
          }} 
          editData={selectedPurchase}
        />
      </div>
    );
  }

  if (activeTab === 'add_purchase_return') {
    return (
      <div className="products-container">
        <AddPurchaseReturn onBack={() => setActiveTab('purchase_return')} />
      </div>
    );
  }

  if (activeTab === 'add_purchase_requisition') {
    return (
      <div className="products-container">
        <AddPurchaseRequisition onBack={() => setActiveTab('purchase_requisition')} />
      </div>
    );
  }

  if (activeTab === 'purchase_order') {
    return <PurchaseOrder />;
  }

  if (activeTab === 'purchase_requisition') {
    return <PurchaseRequisition />;
  }

  if (activeTab === 'purchase_return') {
    return <ListPurchaseReturn />;
  }

  return (
    <div className="products-container">
      <div className="main-card">
        {/* Tabs & Search Header */}


        {/* Table Controls */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={handleAdd}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {/* Table Content */}
        {activeTab === 'list_purchases' && (
          isLoading ? (
            <div className="loading-sales">
              <h3>⏳ Loading Purchases...</h3>
              <p>Fetching purchases data from server...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="no-sales">
              <h3>📊 No Purchases Found</h3>
              <p>No purchases have been recorded yet.</p>
            </div>
          ) : (
            <>
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      {[
                        { key: 'transaction_date', label: 'Date' },
                        { key: 'ref_no', label: 'Reference No' },
                        { key: 'location', label: 'Location' },
                        { key: 'supplier', label: 'Supplier' },
                        { key: 'status', label: 'Purchase Status' },
                        { key: 'payment_status', label: 'Payment Status' },
                        { key: 'final_total', label: 'Grand Total' },
                        { key: 'payment_due', label: 'Payment Due' },
                        { key: 'added_by', label: 'Added By' },
                      ].map(({ key, label }) => (
                        <th key={key} className="sortable" onClick={() => handleSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
                            {label}
                            <SortIcon colKey={key} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedPurchases(purchases).map((purchase) => (
                      <tr key={purchase.id} onClick={() => handleView(purchase)} style={{ cursor: 'pointer' }}>
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="action-dropdown-container">
                            <button className="action-btn-trigger" onClick={(e) => toggleDropdown(purchase.id, e)}>
                              Actions <span className="arrow-down">▼</span>
                            </button>
                            {openActionId === purchase.id && ReactDOM.createPortal(
                               <div
                                 className="action-dropdown-menu"
                                 style={{
                                   position: 'fixed',
                                   top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                   bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                   left: `${dropdownPosition.left}px`,
                                   maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                   zIndex: 9999999
                                 }}
                               >
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleView(purchase)}
                                 >
                                   👁️ View
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handlePrint(purchase)}
                                 >
                                   🖨️ Print
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleEdit(purchase)}
                                 >
                                   ✏️ Edit
                                 </button>
                                 <button
                                   className="dropdown-item delete-item"
                                   onClick={() => handleDelete(purchase.id)}
                                 >
                                   🗑️ Delete
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleOpenLabels(purchase)}
                                 >
                                   🏷️ Labels
                                 </button>
                                 
                                 <div className="divider"></div>
                                 
                                 {purchase.payment_status?.toLowerCase() !== 'paid' && (
                                   <button
                                     className="dropdown-item"
                                     onClick={() => handleAddPayment(purchase)}
                                   >
                                     💳 Add Payment
                                   </button>
                                 )}
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleViewPayments(purchase)}
                                 >
                                   💰 View Payments
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleUpdateStatus(purchase)}
                                 >
                                   ✅ Update Status
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleShowNotification(purchase)}
                                 >
                                   ✉️ Items Pending Notification
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => alert('Purchase Return functionality coming soon!')}
                                 >
                                   ↩️ Purchase Return
                                 </button>
                               </div>,
                               document.body
                             )}
                          </div>
                        </td>

                        <td>{purchase.transaction_date || 'N/A'}</td>
                        <td>{purchase.ref_no || 'N/A'}</td>
                        <td>{purchase.location?.name || 'N/A'}</td>
                        <td>{purchase.supplier?.name || purchase.contact?.name || 'N/A'}</td>
                        <td><span className={`payment-status-badge ${purchase.status?.toLowerCase()}`}>{purchase.status || 'Pending'}</span></td>
                        <td><span className={`payment-status-badge ${purchase.payment_status?.toLowerCase()}`}>{purchase.payment_status || 'Due'}</span></td>
                        <td>${parseFloat(purchase.final_total || 0).toFixed(2)}</td>
                        <td className="payment-due-cell">
                          <div style={{ fontSize: '0.85rem' }}>
                            <span style={{ color: '#64748b' }}>P:</span> ${parseFloat(purchase.payment_due || 0).toFixed(2)}
                          </div>
                          {purchase.return_transaction && parseFloat(purchase.return_transaction.amount_due) > 0 && (
                            <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                              <span style={{ color: '#ef4444' }}>R:</span> ${parseFloat(purchase.return_transaction.amount_due).toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td>{resolveAddedBy(purchase)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination (Simplified for now) */}
              {purchases.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                  <div>Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalPurchases || purchases.length)} of {totalPurchases || purchases.length} entries</div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                    <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>Page {currentPage} of {lastPage}</span>
                    <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))} disabled={currentPage === lastPage}>Next</button>
                  </div>
                </div>
              )}
            </>
          )
        )}

        {/* Placeholders for other tabs */}
        {activeTab !== 'list_purchases' && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
            <h3>{activeTab.replace(/_/g, ' ').toUpperCase()}</h3>
            <p>Content for {activeTab.replace(/_/g, ' ')} will be displayed here.</p>
            <p>Check "List Purchases" tab for the implemented layout.</p>
            {/* Reuse table structure for visual consistency even if empty */}
            <div className="products-table-container" style={{ marginTop: '1rem' }}>
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Reference No</th>
                    <th>Date</th>
                    <th>Supplier</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>No data available for this section yet.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Filter Modal (Shared) */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Status:</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="received">Received</option>
                    <option value="partial">Partial</option>
                    <option value="ordered">Ordered</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Payment Status:</label>
                  <select value={filterPaymentStatus} onChange={(e) => setFilterPaymentStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="due">Due</option>
                    <option value="partial">Partial</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Supplier:</label>
                  <select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)}>
                    <option value="all">All</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name || s.supplier_business_name || s.contact_id || `ID: ${s.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Location:</label>
                  <select value={filterLocationId} onChange={(e) => setFilterLocationId(e.target.value)}>
                    <option value="all">All</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name || `ID: ${l.id}`}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer purchase-filter-footer">
              <button className="modal-btn close-btn" onClick={() => {
                setFilterStatus('all');
                setFilterPaymentStatus('all');
                setFilterSupplierId('all');
                setFilterLocationId('all');
                setCurrentPage(1);
              }}>Reset</button>
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
              <button className="modal-btn primary-btn" onClick={() => { setCurrentPage(1); setShowFilters(false); fetchPurchases(); }}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}
      {/* View Purchase Modal */}
      {showViewModal && selectedPurchase && (
        <ViewPurchaseModal
          purchase={selectedPurchase}
          isLoading={isLoadingView}
          onClose={() => setShowViewModal(false)}
          onPrint={handlePrint}
        />
      )}
      
      {/* Label Generator Modal */}
      <LabelGenerator
        isOpen={showLabelModal}
        onClose={() => {
          setShowLabelModal(false);
          setProductsForLabels([]);
        }}
        initialProducts={productsForLabels}
      />

      {/* Add Payment Modal */}
      {showAddPaymentModal && selectedPurchase && (
        <AddPaymentModal
          sale={selectedPurchase}
          onClose={() => {
            setShowAddPaymentModal(false);
            setSelectedPurchase(null);
          }}
          onSuccess={() => {
            fetchPurchases();
          }}
        />
      )}

      {/* View Payments Modal */}
      {showViewPaymentsModal && selectedPurchase && (
        <ViewPaymentsModal
          show={showViewPaymentsModal}
          onClose={() => setShowViewPaymentsModal(false)}
          transaction={selectedPurchase}
          onPaymentDeleted={fetchPurchases}
        />
      )}

      {showUpdateStatusModal && selectedPurchase && (
        <UpdatePurchaseStatusModal
          show={showUpdateStatusModal}
          onClose={() => setShowUpdateStatusModal(false)}
          purchase={selectedPurchase}
          onSuccess={fetchPurchases}
        />
      )}

      {showNotificationModal && selectedPurchase && (
        <PurchaseNotificationModal
          purchase={selectedPurchase}
          onClose={() => setShowNotificationModal(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  </div>
);
};

export default Purchases;
